/**
 * AHRS Admin — API client (jQuery AJAX)
 *
 * Central HTTP wrapper with:
 * - Access-token management (in-memory, never localStorage)
 * - Automatic 401 → refresh → retry
 * - Consistent error handling
 */
import $ from 'jquery'

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000/api'

// ─── Token + user store (in-memory only — XSS-safe) ─────────────────────────
let accessToken = null
let currentUser = null

export function setToken(token) { accessToken = token }
export function getToken()      { return accessToken }
export function clearToken()    { accessToken = null; currentUser = null }

export function setUser(user) { currentUser = user }
export function getUser()     { return currentUser }

// ─── Core request function ───────────────────────────────────────────────────

function request(method, url, data, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const ajaxOpts = {
    method,
    url: fullUrl,
    contentType: 'application/json',
    dataType: 'json',
    xhrFields: { withCredentials: true }, // send httpOnly cookies
    ...options
  }

  // Attach Bearer token if we have one
  if (accessToken) {
    ajaxOpts.headers = {
      ...ajaxOpts.headers,
      Authorization: `Bearer ${accessToken}`
    }
  }

  // Attach body for POST/PUT/DELETE — always send at least '{}' to avoid
  // Fastify rejecting an empty body when Content-Type is application/json
  if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
    ajaxOpts.data = JSON.stringify(data || {})
  }

  // Attach query params for GET
  if (data && method === 'GET') {
    ajaxOpts.data = data
  }

  return new Promise((resolve, reject) => {
    $.ajax(ajaxOpts)
      .done((resp) => resolve(resp))
      .fail((xhr) => {
        const err = {
          status: xhr.status,
          message: xhr.responseJSON?.error || xhr.statusText || 'Network error',
          retryAfter: xhr.responseJSON?.retryAfter || null
        }

        // 401 on a non-auth route → try refreshing the token
        if (xhr.status === 401 && !url.includes('/auth/')) {
          return refreshAndRetry(method, url, data, options)
            .then(resolve)
            .catch(reject)
        }

        reject(err)
      })
  })
}

// ─── Refresh token and retry original request ────────────────────────────────

let refreshPromise = null

function refreshAndRetry(method, url, data, options) {
  // Deduplicate concurrent refresh calls
  if (!refreshPromise) {
    refreshPromise = request('POST', '/auth/refresh')
      .then((resp) => {
        accessToken = resp.accessToken
        refreshPromise = null
        return resp
      })
      .catch((err) => {
        refreshPromise = null
        clearToken()
        // Don't hard-redirect — let the router guard handle it
        throw err
      })
  }

  return refreshPromise.then(() => request(method, url, data, options))
}

// ─── Fetch a file as a blob URL (for authenticated file display) ─────────────

export function fetchBlobUrl(url) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', fullUrl, true)
    xhr.responseType = 'blob'
    xhr.withCredentials = true
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(URL.createObjectURL(xhr.response))
      } else {
        reject(new Error('Failed to load file'))
      }
    }
    xhr.onerror = () => reject(new Error('Network error'))
    xhr.send()
  })
}

// ─── Open an authenticated file in a new tab ─────────────────────────────────
// Like fetchBlobUrl, but opens the (full-resolution) file in a new browser tab.
// Used for "view full" on documents whose inline preview shows a thumbnail.

// Loader page shown in the new tab while the authenticated document is fetched,
// so the user sees immediate feedback instead of a blank tab that only fills in
// once the (possibly large) file has finished downloading.
const NEW_TAB_LOADER_HTML = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Loading document…</title>
<style>
  html,body{height:100%;margin:0}
  body{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;
    font-family:'Inter','Segoe UI',Arial,sans-serif;background:#f6f8f7;color:#1a472a}
  .spinner{width:44px;height:44px;border:4px solid #d1e7dd;border-top-color:#119548;border-radius:50%;
    animation:spin .7s linear infinite}
  .msg{font-size:15px;font-weight:600;letter-spacing:.2px}
  .sub{font-size:12.5px;color:#6b7280;margin-top:-8px}
  @keyframes spin{to{transform:rotate(360deg)}}
  @media (prefers-reduced-motion:reduce){.spinner{animation:none}}
</style></head>
<body>
  <div class="spinner" id="sp"></div>
  <div class="msg" id="msg">Loading document…</div>
  <div class="sub">It will open here automatically once ready.</div>
</body></html>`

export function openBlobInNewTab(url) {
  // Open the tab synchronously inside the click gesture (so popup blockers don't
  // stop it) and paint a loader; then swap to the document once it's fetched.
  const win = window.open('', '_blank')
  if (win) {
    try { win.document.write(NEW_TAB_LOADER_HTML); win.document.close() } catch (e) { /* ignore */ }
  }
  return fetchBlobUrl(url).then(objUrl => {
    if (win && !win.closed) win.location.replace(objUrl)
    else window.open(objUrl, '_blank') // popup was blocked — fall back
    // Revoke once the tab has had time to load the blob.
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000)
    return win
  }).catch(err => {
    // Surface the failure in the already-open tab instead of leaving a spinner.
    if (win && !win.closed) {
      try {
        win.document.getElementById('sp').style.display = 'none'
        win.document.getElementById('msg').textContent =
          'Could not load the document. Please close this tab and try again.'
      } catch (e) { try { win.close() } catch (e2) { /* ignore */ } }
    }
    throw err
  })
}

// ─── Authenticated file download (CSV export, etc.) ──────────────────────────
// A top-level window.open() can't carry the in-memory Bearer token, so authed
// downloads must go through an XHR that sets the header, then save the blob.

export function downloadFile(url, filename) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', fullUrl, true)
    xhr.responseType = 'blob'
    xhr.withCredentials = true
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.onload = () => {
      if (xhr.status === 200) {
        const objUrl = URL.createObjectURL(xhr.response)
        const link = document.createElement('a')
        link.href = objUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objUrl)
        resolve()
      } else {
        reject(new Error('Failed to download file'))
      }
    }
    xhr.onerror = () => reject(new Error('Network error'))
    xhr.send()
  })
}

// ─── Multipart upload (for tour registration files) ──────────────────────────

export function upload(url, formData, method = 'POST') {
  const fullUrl = `${BASE_URL}${url}`

  return new Promise((resolve, reject) => {
    $.ajax({
      method,
      url: fullUrl,
      data: formData,
      processData: false,
      contentType: false,
      xhrFields: { withCredentials: true },
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    })
      .done((resp) => resolve(resp))
      .fail((xhr) => {
        reject({
          status: xhr.status,
          message: xhr.responseJSON?.error || xhr.statusText || 'Upload failed'
        })
      })
  })
}

// ─── Convenience methods ─────────────────────────────────────────────────────

export const api = {
  get:    (url, params)  => request('GET', url, params),
  post:   (url, data)    => request('POST', url, data),
  put:    (url, data)    => request('PUT', url, data),
  del:    (url, data)    => request('DELETE', url, data),
  upload
}

export default api
